import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { twoFactorAPI } from '../services/api';
import { Lock, Building2, Shield, HelpCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ============================================================================
// Base64URL helpers for WebAuthn
// ============================================================================
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ============================================================================
// Spinner component used throughout
// ============================================================================
const Spinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

interface TenantInfo {
  id: number;
  name: string;
  slug: string;
}

// ============================================================================
// WebAuthn Challenge Screen
// ============================================================================
const WebAuthnChallenge: React.FC<{
  tempToken: string;
  rememberDevice: boolean;
  setRememberDevice: (v: boolean) => void;
  onSuccess: (data: any) => void;
  onSwitchToEmail: () => void;
  onBack: () => void;
  error: string;
  setError: (e: string) => void;
}> = ({ tempToken, rememberDevice, setRememberDevice, onSuccess, onSwitchToEmail, onBack, error, setError }) => {
  const [verifying, setVerifying] = useState(false);
  const [autoTriggered, setAutoTriggered] = useState(false);

  const triggerWebAuthn = useCallback(async () => {
    setVerifying(true);
    setError('');
    try {
      // Step 1: Get auth options
      const options = await twoFactorAPI.webauthnAuthOptions(tempToken);

      // Step 2: Prepare publicKey for browser
      const publicKey: PublicKeyCredentialRequestOptions = {
        ...options.publicKey,
        challenge: base64urlToBuffer(options.publicKey.challenge),
        allowCredentials: (options.publicKey.allowCredentials || []).map((cred: any) => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        })),
      };

      // Step 3: Trigger browser biometric prompt
      const assertion = await navigator.credentials.get({ publicKey }) as PublicKeyCredential;
      if (!assertion) {
        setError('Verification was cancelled');
        return;
      }

      const assertionResponse = assertion.response as AuthenticatorAssertionResponse;

      // Step 4: Send to backend
      const credentialData = {
        id: assertion.id,
        rawId: bufferToBase64url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
          clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
          signature: bufferToBase64url(assertionResponse.signature),
          userHandle: assertionResponse.userHandle ? bufferToBase64url(assertionResponse.userHandle) : null,
        },
      };

      const result = await twoFactorAPI.webauthnAuthComplete(tempToken, credentialData, rememberDevice);
      onSuccess(result);
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Verification was cancelled or not allowed by the browser.');
      } else {
        setError(err.response?.data?.detail || 'Verification failed. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  }, [tempToken, rememberDevice, onSuccess, setError]);

  // Auto-trigger on mount
  useEffect(() => {
    if (!autoTriggered) {
      setAutoTriggered(true);
      triggerWebAuthn();
    }
  }, [autoTriggered, triggerWebAuthn]);

  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-content-primary mb-2">Verify Your Identity</h2>
      <p className="text-sm text-gray-500 mb-6">
        Please use your fingerprint or Face ID to continue.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {verifying ? (
        <div className="flex items-center justify-center gap-2 text-primary-600 mb-6">
          <Spinner className="h-5 w-5" />
          <span className="text-sm font-medium">Waiting for verification...</span>
        </div>
      ) : (
        <button
          onClick={triggerWebAuthn}
          className="w-full btn-primary py-3 mb-4"
        >
          Try Again
        </button>
      )}

      {/* Remember device checkbox */}
      <label className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={rememberDevice}
          onChange={(e) => setRememberDevice(e.target.checked)}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        Remember this device for 30 days
      </label>

      <div className="space-y-2">
        <button
          onClick={onSwitchToEmail}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Use email code instead
        </button>
        <div>
          <button
            onClick={onBack}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Email OTP Challenge Screen
// ============================================================================
const EmailOTPChallenge: React.FC<{
  tempToken: string;
  rememberDevice: boolean;
  setRememberDevice: (v: boolean) => void;
  onSuccess: (data: any) => void;
  onBack: () => void;
  error: string;
  setError: (e: string) => void;
}> = ({ tempToken, rememberDevice, setRememberDevice, onSuccess, onBack, error, setError }) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Send OTP on mount
  useEffect(() => {
    const sendOTP = async () => {
      try {
        await twoFactorAPI.emailOtpSend(tempToken);
      } catch (err: any) {
        // Only show error if it's not a "already sent" type
        if (err.response?.status !== 429) {
          setError(err.response?.data?.detail || 'Failed to send verification code');
        }
      }
    };
    sendOTP();
  }, [tempToken, setError]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        handleVerify(code);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || '';
      }
      setDigits(newDigits);

      // Focus the next empty input or last input
      const nextEmpty = newDigits.findIndex((d) => !d);
      const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit if all 6 digits pasted
      if (pasted.length === 6) {
        handleVerify(pasted);
      }
    }
  };

  const handleVerify = async (code?: string) => {
    const finalCode = code || digits.join('');
    if (finalCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');
    try {
      const result = await twoFactorAPI.emailOtpVerify(tempToken, finalCode, rememberDevice);
      onSuccess(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid verification code. Please try again.');
      // Clear digits on error
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendSuccess(false);
    setError('');
    try {
      await twoFactorAPI.emailOtpSend(tempToken);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('Please wait before requesting another code.');
      } else {
        setError(err.response?.data?.detail || 'Failed to resend code');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-content-primary mb-2">Enter Verification Code</h2>
      <p className="text-sm text-gray-500 mb-6">
        A 6-digit code was sent to your email.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {resendSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
          A new code has been sent to your email.
        </div>
      )}

      {/* 6-digit OTP input */}
      <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputRefs.current[index] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={verifying}
            className="w-11 h-13 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors disabled:bg-gray-100 disabled:text-gray-400"
            autoComplete="one-time-code"
          />
        ))}
      </div>

      {/* Verify button */}
      <button
        onClick={() => handleVerify()}
        disabled={verifying || digits.join('').length !== 6}
        className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
      >
        {verifying ? (
          <span className="flex items-center justify-center gap-2">
            <Spinner className="h-5 w-5" />
            Verifying...
          </span>
        ) : (
          'Verify'
        )}
      </button>

      {/* Remember device checkbox */}
      <label className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={rememberDevice}
          onChange={(e) => setRememberDevice(e.target.checked)}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        Remember this device for 30 days
      </label>

      <div className="space-y-2">
        <div className="text-sm text-gray-500">
          Didn't receive a code?{' '}
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
          >
            {resending ? 'Sending...' : 'Resend'}
          </button>
        </div>
        <div>
          <button
            onClick={onBack}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Login Component
// ============================================================================
const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingOrg, setValidatingOrg] = useState(false);
  const [validatedOrg, setValidatedOrg] = useState<{ id: number; name: string; slug?: string } | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [tenantList, setTenantList] = useState<TenantInfo[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 2FA state
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState<'webauthn' | 'email_otp'>('email_otp');
  const [tempToken, setTempToken] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);

  const { login, complete2FALogin } = useAuth();
  const navigate = useNavigate();

  // Fetch tenant list once on mount
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/tenants/public`);
        setTenantList(response.data);
        setTenantsLoaded(true);
      } catch (err: any) {
        if (err.response?.status === 429) {
          const retryAfter = err.response.headers['retry-after'];
          const waitSec = retryAfter ? parseInt(retryAfter) : 60;
          setError(`Too many requests. Please wait ${waitSec} seconds and refresh the page.`);
        }
        // Still allow system admin login even if API fails
        setTenantsLoaded(true);
      }
    };
    fetchTenants();
  }, []);

  // Validate organization code locally against cached tenant list
  const validateOrgCode = (code: string) => {
    if (!code || code.trim() === '') {
      setValidatedOrg(null);
      return;
    }

    const trimmed = code.trim().toLowerCase();

    // Check for system admin codes
    if (trimmed === 'admin' || trimmed === 'system') {
      setValidatedOrg({ id: -1, name: 'System Administrator' });
      setError('');
      return;
    }

    // Check against cached tenant list
    const found = tenantList.find((t) => t.slug.toLowerCase() === trimmed);
    if (found) {
      setValidatedOrg({ id: 0, name: found.name, slug: found.slug });
      setError('');
    } else {
      setValidatedOrg(null);
      if (tenantsLoaded) {
        setError('Invalid organization code');
      }
    }
  };

  // Debounced validation on org code change
  const handleOrgCodeChange = (value: string) => {
    setOrgCode(value);
    setValidatedOrg(null);
    setError('');

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (value.trim()) {
        setValidatingOrg(true);
        validateOrgCode(value.trim());
        setValidatingOrg(false);
      }
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Organization code is required
    if (!orgCode.trim()) {
      setError('Organization code is required. Enter your org code or "admin" for system login.');
      return;
    }
    if (!validatedOrg) {
      setError('Please enter a valid organization code');
      return;
    }

    setLoading(true);

    try {
      // Use tenant slug for login (never expose internal IDs)
      const tenantSlug = validatedOrg?.id === -1 ? null : (validatedOrg?.slug || null);

      const result = await login(username, password, undefined, tenantSlug);

      // Check if 2FA is required
      if (result && result.requires_2fa) {
        setTempToken(result.temp_token);
        setTwoFAMethod(result.two_fa_method);
        setTwoFARequired(true);
        return;
      }

      navigate('/');
    } catch (err: any) {
      const msg = err.message || 'Invalid username or password';
      if (msg.includes('429') || msg.toLowerCase().includes('too many') || msg.toLowerCase().includes('rate limit')) {
        setError('Too many login attempts. Please wait 1 minute before trying again.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = async (data: any) => {
    try {
      await complete2FALogin(data.access_token, data.refresh_token, data.device_token);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to complete login');
    }
  };

  const handleBackToLogin = () => {
    setTwoFARequired(false);
    setTempToken('');
    setTwoFAMethod('email_otp');
    setRememberDevice(false);
    setError('');
  };

  const handleSwitchToEmail = async () => {
    setTwoFAMethod('email_otp');
    setError('');
  };

  const isSystemAdmin = validatedOrg?.id === -1;

  return (
    <div className="min-h-screen bg-surface-secondary flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8 border border-surface-border">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <img
              src="/kelzix-logo.png"
              alt="KELZIX(TM)"
              className="w-full max-w-[280px] sm:max-w-[320px] h-auto mx-auto mb-4"
              onError={(e) => {
                // Fallback to text if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-600 rounded-lg mb-4">
                <Lock size={28} className="text-white" />
              </div>
              <h1 className="text-2xl font-semibold text-content-primary mb-1">
                KELZIX<sup className="text-xs">TM</sup>
              </h1>
            </div>
            <p className="text-content-secondary text-sm">Cold Chain Monitor</p>
          </div>

          {/* 2FA Challenge Screens */}
          {twoFARequired ? (
            <>
              {twoFAMethod === 'webauthn' ? (
                <WebAuthnChallenge
                  tempToken={tempToken}
                  rememberDevice={rememberDevice}
                  setRememberDevice={setRememberDevice}
                  onSuccess={handle2FASuccess}
                  onSwitchToEmail={handleSwitchToEmail}
                  onBack={handleBackToLogin}
                  error={error}
                  setError={setError}
                />
              ) : (
                <EmailOTPChallenge
                  tempToken={tempToken}
                  rememberDevice={rememberDevice}
                  setRememberDevice={setRememberDevice}
                  onSuccess={handle2FASuccess}
                  onBack={handleBackToLogin}
                  error={error}
                  setError={setError}
                />
              )}
            </>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Organization Code Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center justify-between">
                      <span>
                        <Building2 size={16} className="inline mr-1" />
                        Organization Code
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowHelp(!showHelp)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <HelpCircle size={16} />
                      </button>
                    </div>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your organization code"
                    value={orgCode}
                    onChange={(e) => handleOrgCodeChange(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className={`input ${
                      validatedOrg && !isSystemAdmin ? 'border-status-ok bg-status-ok-light' :
                      error && orgCode ? 'border-status-critical bg-status-critical-light' : ''
                    }`}
                    disabled={loading}
                  />
                  {validatingOrg && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Spinner className="h-3 w-3" />
                      Validating...
                    </p>
                  )}
                </div>

                {/* Help Text */}
                {showHelp && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600">
                    <p className="font-medium mb-1">Organization Code</p>
                    <p>Enter the unique code provided by your administrator.</p>
                    <p className="mt-2 text-xs text-gray-500">
                      For system administrators, use: <span className="font-mono bg-gray-200 px-1 rounded">admin</span>
                    </p>
                  </div>
                )}

                {/* System Admin Badge */}
                {isSystemAdmin && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 text-purple-700">
                      <Shield size={18} />
                      <span className="font-medium">System Administrator Login</span>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">
                      Access to manage all tenants and system settings
                    </p>
                  </div>
                )}

                {/* Validated Organization Badge */}
                {validatedOrg && !isSystemAdmin && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <Building2 size={18} />
                      <span className="font-medium">{validatedOrg.name}</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      Code: <span className="font-mono">{orgCode.toUpperCase()}</span>
                    </p>
                  </div>
                )}

                {/* Username */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-content-secondary mb-1.5">
                    Username
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="input"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Password */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-content-secondary mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    className="input"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={loading || validatingOrg}
                  className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner className="h-5 w-5" />
                      Logging in...
                    </span>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>

              {/* Forgot Password */}
              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Forgot your password?
                </Link>
              </div>

              {/* Help Text */}
              <div className="mt-6 text-center space-y-2">
                <p className="text-sm text-gray-500">
                  Contact your administrator for your organization code
                </p>
                <a
                  href="mailto:admin@kelzix.com"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Having trouble? Contact support
                </a>
              </div>
            </>
          )}

          {/* Trust Elements */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <span>Powered by</span>
              <span className="font-semibold text-gray-600">KELZIX</span>
            </div>
            <p className="text-center text-xs text-gray-400 mt-1">
              Your data is encrypted and secure
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          &copy; 2026 KELZIX&trade; Cold Chain Solutions
        </p>
      </div>
    </div>
  );
};

export default Login;
